import { useCallback, useEffect, useState } from "react";

import { useNotifications } from "@/contexts/NotificationsContext";

import { useApi } from "../useApi";

export interface EvalScores {
  sql_validity?: number;
  execution_accuracy?: number;
  stat_fidelity?: number;
  conversational_quality?: number;
  correctness?: number;
  faithfulness?: number;
  harmfulness?: number;
  latency?: number;
  relevance?: number;
  ves?: number;
  token_usage?: number;
  tool_parameter_accuracy?: number;
  tool_selection_accuracy?: number;
  trajectory_quality?: number;
  metadata_filter_accuracy?: number;
}

export interface EvalCases {
  total: number;
  passed: number;
  failed: number;
  errored: number;
}

export interface EvalResult {
  verdict: string;
  scores: EvalScores;
  cases: EvalCases;
  tierAggregates: Record<string, number>;
}

export interface EvalBuild {
  buildId: string;
  buildNumber: number | null;
  status: string;
  startTime: string | null;
  endTime: string | null;
  durationSeconds: number | null;
  triggeredBy: string | null;
  reportBucket: string;
  evalResult: EvalResult | null;
  reportUrl: string | null;
  downloadUrl: string | null;
  logsUrl: string | null;
}

interface TriggerResponse {
  buildId: string;
  projectName: string;
  status: string;
}

interface StatusResponse {
  projectName: string;
  builds: EvalBuild[];
}

/**
 * Data access for the admin "Agent Evaluation" page.
 *
 * Fetches recent eval build statuses (with inline scores from run_manifest.json)
 * from ``GET /admin/eval/status`` and triggers new runs via
 * ``POST /admin/eval/trigger``.
 */
const useAdminEval = () => {
  const [builds, setBuilds] = useState<EvalBuild[]>([]);
  const [projectName, setProjectName] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [triggering, setTriggering] = useState(false);

  const { notifyError, notifySuccess } = useNotifications();
  const { apiGet, apiPost } = useApi({ isAdmin: true });

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiGet<StatusResponse>("/admin/eval/status");
      if (response) {
        setBuilds(response.builds ?? []);
        setProjectName(response.projectName ?? "");
      }
    } catch (error) {
      console.error(error);
      notifyError("Evaluation Error", "Failed to load evaluation status");
    } finally {
      setLoading(false);
    }
  }, [apiGet, notifyError]);

  const triggerEval = useCallback(async () => {
    setTriggering(true);
    try {
      const response = await apiPost<TriggerResponse>(
        "/admin/eval/trigger",
        {}
      );
      if (response) {
        notifySuccess(
          `Evaluation started (Build ${response.buildId.split(":").pop()}). It typically takes 20-25 minutes.`
        );
        // Refresh status after triggering
        await loadStatus();
      }
    } catch (error: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      const message =
        error?.message || "Failed to trigger evaluation";
      if (error?.statusCode === 409) {
        notifyError("Evaluation In Progress", message);
      } else {
        notifyError("Evaluation Error", message);
      }
    } finally {
      setTriggering(false);
    }
  }, [apiPost, notifyError, notifySuccess, loadStatus]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  return {
    builds,
    projectName,
    loading,
    triggering,
    loadStatus,
    triggerEval,
  };
};

export default useAdminEval;
